import React from 'react';
import { Database, AlertCircle, SearchX, CloudOff } from 'lucide-react';
import { Button } from '../ui/Button';

type EmptyVariant = 'no-data' | 'error' | 'no-results' | 'offline';

interface EmptyStateProps {
  variant?: EmptyVariant;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const variantConfig: Record<
  EmptyVariant,
  { Icon: React.ElementType; defaultTitle: string; defaultDescription: string; iconColor: string }
> = {
  'no-data': {
    Icon: Database,
    defaultTitle: 'No data available',
    defaultDescription: 'There is no data to display at this time.',
    iconColor: 'var(--text-tertiary)',
  },
  error: {
    Icon: AlertCircle,
    defaultTitle: 'Something went wrong',
    defaultDescription: 'Failed to load data. Check your connection and try again.',
    iconColor: 'var(--accent-danger)',
  },
  'no-results': {
    Icon: SearchX,
    defaultTitle: 'No results found',
    defaultDescription: 'Try adjusting your search or filter criteria.',
    iconColor: 'var(--text-tertiary)',
  },
  offline: {
    Icon: CloudOff,
    defaultTitle: 'Cannot connect to server',
    defaultDescription: 'The backend is unavailable. Check that the server is running on port 3001.',
    iconColor: 'var(--accent-warning)',
  },
};

export function EmptyState({
  variant = 'no-data',
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  const { Icon, defaultTitle, defaultDescription, iconColor } = variantConfig[variant];

  return (
    <div
      className={[
        'flex flex-col items-center justify-center gap-4 py-12 px-6 text-center',
        className,
      ].join(' ')}
    >
      <Icon
        size={40}
        style={{ color: iconColor }}
        strokeWidth={1.5}
      />
      <div className="flex flex-col gap-1 max-w-xs">
        <p
          className="text-[var(--text-h3)] font-[500] text-[var(--text-primary)]"
          style={{ fontSize: '1.0625rem', lineHeight: 1.35 }}
        >
          {title ?? defaultTitle}
        </p>
        <p
          className="text-[var(--text-secondary)]"
          style={{ fontSize: '14px', lineHeight: 1.55 }}
        >
          {description ?? defaultDescription}
        </p>
      </div>
      {action && (
        <Button variant="secondary" onClick={action.onClick} size="sm">
          {action.label}
        </Button>
      )}
    </div>
  );
}
